import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../lib/api';
import type { ApiResponse } from '../types/task';
import type {
  FeedbackReport,
  FeedbackStatus,
  CreateFeedbackInput,
  UpdateFeedbackInput,
} from '../types/feedback';

const FEEDBACK_CHANNEL_KEY = ['system', 'feedback-slack-channel'];

export function useFeedbackList(status?: FeedbackStatus | 'all') {
  return useQuery({
    queryKey: ['feedback', status ?? 'all'],
    queryFn: async () => {
      const params = status && status !== 'all' ? `?status=${status}` : '';
      const res = await api.get<ApiResponse<FeedbackReport[]>>(`/feedback${params}`);
      return res.data;
    },
  });
}

export function useCreateFeedback() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: CreateFeedbackInput) => {
      const res = await api.post<ApiResponse<FeedbackReport>>('/feedback', input);
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['feedback'] });
    },
  });
}

export function useUpdateFeedback() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, ...input }: UpdateFeedbackInput & { id: string }) => {
      const res = await api.patch<ApiResponse<FeedbackReport>>(`/feedback/${id}`, input);
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['feedback'] });
    },
  });
}

export function useDeleteFeedback() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/feedback/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['feedback'] });
    },
  });
}

export function useFeedbackSlackChannel() {
  return useQuery({
    queryKey: FEEDBACK_CHANNEL_KEY,
    queryFn: async () => {
      const res = await api.get<{ data: { channelId: string; channelName: string } | null }>(
        '/system/feedback-slack-channel',
      );
      return res.data;
    },
    staleTime: 60_000,
  });
}

export function useSaveFeedbackSlackChannel() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (channel: { channelId: string; channelName: string } | null) => {
      await api.put('/system/feedback-slack-channel', channel ?? { channelId: null, channelName: null });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: FEEDBACK_CHANNEL_KEY });
    },
  });
}
